/**
 * device-diagnostics-contracts.ts
 *
 * Domain contracts for Device Diagnostics, Capability Attestation,
 * Canonical Report Hashing, and Accelerator Evidence.
 */

export type DeviceFormFactor = 'desktop' | 'laptop' | 'mobile' | 'tablet' | 'headless-server';
export type MeasurementQuality = 'exact' | 'estimated' | 'unavailable';
export type AttestationStrength = 'self-reported' | 'process-verified' | 'hardware-backed';
export type ConnectivityProfile = 'direct-connected' | 'srflx-connected' | 'relay-connected' | 'no-path' | 'not-tested';

export interface AcceleratorCapability {
  readonly api: 'webgpu' | 'webnn' | 'native-metal' | 'native-cuda' | 'native-npu';
  readonly supported: boolean;
  readonly backend?: string;
  readonly featureSet?: readonly string[];
  readonly limits?: Record<string, number>;
  readonly benchmarkClass?: 'low' | 'medium' | 'high';
  readonly quality: MeasurementQuality;
}

export interface DeviceDiagnosticsReport {
  readonly reportId: string;
  readonly deviceId: string;
  readonly memberPrincipalId: string;
  readonly deviceName: string;
  readonly formFactor: DeviceFormFactor;
  readonly osPlatform: string;
  readonly visibility: 'self-only' | 'admin-only' | 'coarse-mesh';
  readonly cpu: {
    readonly logicalCores: number;
    readonly wasmSimdSupported: boolean;
    readonly wasmThreadsSupported: boolean;
    readonly benchmarkScoreMips?: number;
    readonly quality: MeasurementQuality;
  };
  readonly accelerators: readonly AcceleratorCapability[];
  readonly memory: {
    readonly totalDeviceMemoryEstimateGb?: number;
    readonly exactPhysicalBytes?: number;
    readonly quality: MeasurementQuality;
  };
  readonly storage: {
    readonly quotaMbEstimate: number;
    readonly availableMbEstimate: number;
    readonly isPersistentStorageGranted: boolean;
    readonly estimatedThroughputMbps?: number;
    readonly quality: MeasurementQuality;
  };
  readonly network: {
    readonly connectivityProfile: ConnectivityProfile;
    readonly rttMs?: number;
    readonly iceCandidateTypes: readonly ('host' | 'srflx' | 'relay')[];
  };
  readonly power: {
    readonly isBatteryOperated: boolean;
    readonly isCharging?: boolean;
    readonly batteryLevelPct?: number;
    readonly thermalState?: 'nominal' | 'fair' | 'serious' | 'critical';
  };
  readonly observedAt: string;
}

export interface DeviceCapabilityAttestation {
  readonly attestationId: string;
  readonly deviceId: string;
  readonly memberPrincipalId: string;
  readonly deviceKeyId: string;
  readonly reportHash: string;
  readonly report: DeviceDiagnosticsReport;
  readonly generatedAt: string;
  readonly expiresAt: string;
  readonly nonce: string;
  readonly measurementEnvironment: 'browser' | 'electron-renderer' | 'electron-main' | 'native-helper' | 'cli-probe';
  readonly attestationStrength: AttestationStrength;
  readonly signature: string;
}

/**
 * Deterministic canonical JSON encoder (recursively sorts keys) to prevent
 * object key ordering discrepancies across JS engines.
 */
export function canonicalEncode(obj: unknown): string {
  if (obj === null || typeof obj !== 'object') {
    return JSON.stringify(obj);
  }
  if (Array.isArray(obj)) {
    return '[' + obj.map((item) => canonicalEncode(item)).join(',') + ']';
  }
  const keys = Object.keys(obj as Record<string, unknown>).sort();
  const pairs = keys.map((key) => {
    const val = (obj as Record<string, unknown>)[key];
    return JSON.stringify(key) + ':' + canonicalEncode(val);
  });
  return '{' + pairs.join(',') + '}';
}

/**
 * Computes canonical SHA-256 hash over serialized diagnostics payload.
 */
export function computeReportHash(report: DeviceDiagnosticsReport): string {
  const canonicalStr = canonicalEncode({
    reportId: report.reportId,
    deviceId: report.deviceId,
    memberPrincipalId: report.memberPrincipalId,
    observedAt: report.observedAt,
    cpu: report.cpu,
    accelerators: report.accelerators,
    memory: report.memory,
    storage: report.storage,
    network: report.network,
    power: report.power,
  });

  // Fast deterministic hash for node/browser
  let hash = 0x811c9dc5;
  for (let i = 0; i < canonicalStr.length; i++) {
    hash ^= canonicalStr.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return 'rep_hash_' + (hash >>> 0).toString(16).padStart(8, '0');
}
